import ApiService from "@/api/ApiService";
import { usePersistantStore } from "@/stores/global";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Fragment } from "react/jsx-runtime";
import { AxiosResponse } from "axios";
import { ArrowBackIosNew, ArrowForwardIos, Block, Check, Info, X } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import LabelWrapper from "../root/LabelWrapper";
import { useEffect, useState } from "react";
import useRights from "@/shared/api/useRights";
import { useHolidayModal } from "./dialogs/HolidayModal";
const formatRange = (a, b) => (a === b ? a : `${a} bis ${b}`);
interface VacationRequest {
  id: number;
  staffMember: { id: number; name: string; firstName: string };
  createdBy: { id: number; name: string; firstName: string };
  start: string;
  end: string;
  comment: string;
  status: { status: ("none" | "rejected" | "accepted")[]; comments: string[]; statusString: "none" | "rejected" | "accepted" };
}

export default function VacationRequests({ calendar }: { calendar?: any }) {
  const setReference = usePersistantStore((state) => state.setReference);
  const setCalendar = (reference, start, end) => {
    setReference(reference);
    calendar.current?.setCalendar(dayjs(start, "YYYY-MM-DD").subtract(1, "day"), dayjs(end, "YYYY-MM-DD").add(6, "day"));
  };

  const { show } = useHolidayModal();
  const [dialogState, setDialogState] = useState(null);

  const sideBarHidden = usePersistantStore((store) => store.getSettings().sideBarHidden);
  const setSideBarHidden = usePersistantStore((store) => (sideBarHidden: boolean) => store.setSettings({ sideBarHidden }));

  const region = usePersistantStore((store) => store.region);
  const userId = usePersistantStore((store) => store.userId);
  const authenticated = usePersistantStore((store) => store.authenticated);
  const rights = useRights();
  useEffect(() => setDialogState(null), [userId]);

  const queryClient = useQueryClient();

  const query = useQuery<VacationRequest[], AxiosResponse>({
    queryKey: ["getVacationRequests", "" + region, userId],
    enabled: authenticated && ("DISPOSITION" in rights || "OBL" in rights),
    gcTime: 0,
    queryFn: ApiService.getVacationRequests(region),
  });

  const respondMutation = useMutation<any, AxiosResponse, { id: number; status: "reject" | "accept"; comment: string }>({
    mutationFn: ApiService.answerVacationRequest(),
    onSettled: (answer) => {
      setDialogState(null);
      queryClient.invalidateQueries({ queryKey: ["getCalendar"] });
      queryClient.invalidateQueries({ queryKey: ["getVacationRequests"] });
    },
  });

  return sideBarHidden ? (
    <Box
      onClick={() => setSideBarHidden(false)}
      sx={{
        position: "absolute",
        bottom: "0",
        right: "0",
        borderTop: "1px solid #aaa",
        backgroundColor: "#fff",
        borderLeft: "1px solid #aaa",
        "&:hover": { backgroundColor: "#eee" },
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: "5px",
      }}
    >
      <ArrowBackIosNew />
    </Box>
  ) : (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        boxShadow: "0px -2px 4px #0000004a",
        maxWidth: "250px",
        borderLeft: "1px solid #888",
        position: "relative",
      }}
    >
      <Box
        sx={{
          overflow: "auto",
          position: "relative",
          background: "#fafafa",
          flex: 1,
          paddingTop: "8px",
        }}
      >
        {query.isLoading ? (
          <CircularProgress />
        ) : query.isLoadingError ? (
          "Error"
        ) : (query?.data ?? []).length === 0 ? (
          <Box sx={{ textAlign: "center", color: "#777", p: 1, fontSize: "14px" }}>Keine offenen Anfragen</Box>
        ) : (
          (query?.data ?? [])
            .sort((a, b) => a.start.localeCompare(b.start))
            .map((x, i, f) => (
              <Fragment key={x.id}>
                {
                  <Box
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        background: "#eee",
                      },
                      display: "flex",
                      flexDirection: "column",
                      border: "1px solid #f55",
                      mb: "8px",
                      mx: "4px",
                      alignItems: "stretch",
                      overflow: "hidden",
                      borderRadius: "5px",
                      flexShrink: 0,
                      fontSize: "13px",
                    }}
                    onClick={() => setCalendar(x.resourceTagId, x.start, x.end)}
                  >
                    <Box className="done" sx={{ display: "flex", borderBottom: "1px solid #777" }}>
                      <Box
                        sx={{
                          flex: 1,
                          p: "4px",
                          overflow: "hidden",
                          lineHeight: 1,
                          textTransform: "uppercase",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {x.staffMember.name}
                      </Box>
                      <Box
                        className="info"
                        onClick={(e) => {
                          e.stopPropagation();
                          show(x.staffMember.id);
                        }}
                        sx={{
                          borderLeft: "1px solid #aaa",
                          color: "#333",
                          boxShadow: "inset -4px 0px 5px rgba(0,0,0,0.1)",
                          p: "4px 6px",
                          fontSize: "14px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          "&:hover": {
                            background: "#abababff",
                          },
                          lineHeight: "14px",

                          background: "#bfbfbfff",
                        }}
                      >
                        <Info fontSize="inherit" />
                      </Box>
                      <Box
                        className="done"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDialogState({ id: x.id, status: "accept", comment: "" });
                        }}
                        sx={{
                          borderLeft: "1px solid #aaa",
                          color: "#333",
                          boxShadow: "inset -4px 0px 5px rgba(0,0,0,0.1)",
                          p: "4px 6px",
                          fontSize: "14px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          "&:hover": {
                            background: "#71c771ff",
                          },
                          lineHeight: "14px",

                          background: "#8df98d",
                        }}
                      >
                        <Check fontSize="inherit" />
                      </Box>
                      <Box
                        className="done"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDialogState({ id: x.id, status: "reject", comment: "" });
                        }}
                        sx={{
                          borderLeft: "1px solid #aaa",
                          color: "#333",
                          boxShadow: "inset -4px 0px 5px rgba(0,0,0,0.1)",
                          p: "4px 6px",
                          fontSize: "14px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          "&:hover": {
                            background: "#c48282ff",
                          },
                          lineHeight: "14px",

                          background: "#f9adad",
                        }}
                      >
                        <Block fontSize="inherit" />
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        lineHeight: 1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        p: "2px",
                        backgroundColor: "#eee",
                      }}
                    >
                      {formatRange(dayjs(x.start).format("DD.MM."), dayjs(x.end).format("DD.MM."))}
                    </Box>

                    {x.comment.length > 0 ? (
                      <Box
                        sx={{
                          display: "-webkit-box",
                          p: "2px",
                          pt: "3px",
                          borderTop: "1px solid #aaa",
                          "-webkit-line-clamp": "2",
                          "-webkit-box-orient": "vertical",
                          overflow: "hidden",
                          fontSize: "12px",
                          lineHeight: 1.1,
                          textAlign: "right",
                          backgroundColor: "#f7f7f7",
                          boxShadow: "0 2px 3px rgba(0,0,0,0.1) inset",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {x.comment}
                      </Box>
                    ) : null}
                  </Box>
                }
              </Fragment>
            ))
        )}
      </Box>
      <Box
        sx={{
          borderTop: "1px solid #aaa",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Box
          onClick={() => setSideBarHidden(true)}
          sx={{
            borderLeft: "1px solid #aaa",
            "&:hover": { backgroundColor: "#eee" },
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: "5px",
          }}
        >
          <ArrowForwardIos />
        </Box>
      </Box>
      {!!dialogState ? (
        <Dialog open={true} maxWidth="sm" fullWidth onClose={() => setDialogState(null)}>
          <DialogTitle>{dialogState.status === "reject" ? "Urlaub ablehnen" : "Urlaub genehmigen"}</DialogTitle>
          <DialogContent>
            <TextField
              size="small"
              fullWidth
              value={dialogState.comment}
              placeholder="Weitere Informationen"
              multiline
              onChange={(e) => {
                setDialogState((s) => ({
                  ...s,
                  comment: (e.target as HTMLInputElement).value,
                }));
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogState(null)}>Abbrechen</Button>
            <LoadingButton
              loading={respondMutation.isPending}
              variant="contained"
              color={dialogState.status === "reject" ? "error" : "success"}
              onClick={() => {
                respondMutation.mutate(dialogState);
              }}
            >
              {dialogState.status === "reject" ? "Ablehnen" : "Genehmigen"}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      ) : null}
    </Box>
  );
}
